import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UserService } from '../../core/services/user.service';
import { ConfirmDialogComponent } from '../../shared/components/confirm-dialog/confirm-dialog.component';
import { ToastComponent } from '../../shared/components/toast/toast.component';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { AuthService } from '../../core/services/auth.service';
import { Router } from '@angular/router';

@Component({
    selector: 'app-profile',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        MatButtonModule,
        MatIconModule,
        MatCardModule,
        MatFormFieldModule,
        MatInputModule
    ],
    templateUrl: './profile.component.html',
    styleUrls: ['./profile.component.css'],
    styles: [`
        .delete-row {
            border-top: 1px solid rgba(0,0,0,0.05);
            margin-top: 4px;
        }
        
        .danger-bg {
            background-color: #ffebee !important;
        }

        .danger-icon {
            color: #d32f2f !important;
        }

        .danger-text {
            color: #d32f2f;
            font-weight: 600;
        }
    `]
})
export class ProfileComponent {
    authService = inject(AuthService);
    router = inject(Router);
    userService = inject(UserService);
    dialog = inject(MatDialog);
    snackBar = inject(MatSnackBar);

    photoURL: string = '';
    displayName: string = '';

    isEditing = false;
    isLoading = false;

    constructor() {
        this.authService.user$.subscribe(user => {
            if (user) {
                this.photoURL = user.photoURL || '';
                this.displayName = user.displayName || '';
            }
        });
    }

    toggleEdit() {
        this.isEditing = !this.isEditing;
        if (!this.isEditing) {
            // Reset if cancelled
            const user = this.authService.getCurrentUser();
            if (user) {
                this.photoURL = user.photoURL || '';
                this.displayName = user.displayName || '';
            }
        }
    }

    async saveProfile() {
        this.isLoading = true;
        try {
            // Only update Display Name here (Photo is handled separately)
            await this.authService.updateProfileData(this.displayName, this.photoURL);
            this.isEditing = false;
        } catch (error) {
            console.error('Error updating profile:', error);
            alert('Failed to update profile');
        } finally {
            this.isLoading = false;
        }
    }



    async deleteAccount() {
        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            panelClass: 'k-bottom-sheet-dialog',
            position: { bottom: '0' },
            data: {
                title: 'Delete Account',
                icon: '⚠️',
                message: 'Are you sure you want to delete your account? This will remove all your data from our servers.',
                warning: 'This action is immediate and cannot be undone.',
                confirmText: 'Delete Everything',
                type: 'delete'
            }
        });
        const result = await dialogRef.afterClosed().toPromise();
        if (!result) return;

        // Double confirm for safety using MatDialog
        const secondDialogRef = this.dialog.open(ConfirmDialogComponent, {
            panelClass: 'k-bottom-sheet-dialog',
            position: { bottom: '0' },
            data: {
                title: 'Final Confirmation',
                icon: '🛑',
                message: 'Last chance! All your expenses, habits, and category data will be lost forever. Still proceed?',
                confirmText: 'Yes, Delete All',
                type: 'delete'
            }
        });
        const secondResult = await secondDialogRef.afterClosed().toPromise();
        if (!secondResult) return;

        this.isLoading = true;
        try {
            // Attempt to delete firestore data first
            try {
                await this.userService.deleteAccount();
            } catch (dataError) {
                console.error('Data cleanup incomplete:', dataError);
                // Proceed to delete auth user anyway so they aren't stuck
            }

            await this.authService.deleteUser();
            await this.authService.logout(); // Ensure full cleanup
            this.showToast('Success', 'Account deleted.', 'success');
            this.router.navigate(['/login']);
        } catch (e: any) {
            // Extract error message/code safely
            const errCode = e.code || '';
            const errMsg = e.message || '';

            // If sensitive operation needs recent login
            if (errCode === 'auth/requires-recent-login' || errMsg.includes('CREDENTIAL_TOO_OLD_LOGIN_AGAIN')) {
                this.showToast('Info', 'Security check: Please sign in again to confirm deletion.', 'success');
                await this.authService.logout();
                this.router.navigate(['/login']);
            } else {
                this.showToast('Error', 'Failed to delete: ' + errMsg, 'error');
            }
        } finally {
            this.isLoading = false;
        }
    }

    private showToast(title: string, message: string, type: 'success' | 'error') {
        this.snackBar.openFromComponent(ToastComponent, {
            data: { title, message, type },
            duration: 3000,
            panelClass: 'custom-toast-container',
            horizontalPosition: 'end',
            verticalPosition: 'bottom'
        });
    }

    logout() {
        this.authService.logout().then(() => this.router.navigate(['/login']));
    }
}
